/**
 * useHotkeys — page-level keyboard shortcuts (Tally-style F-keys, Ctrl+A …).
 *
 *   useHotkeys({
 *       "f5": () => setType("PAID"),
 *       "ctrl+a": save,          // ctrl matches ⌘ on macOS too
 *       "alt+c": createParty,
 *   }, { enabled: !loading, when: () => formContains(document.activeElement) });
 *
 * - One native listener on `window` in the BUBBLE phase, so a field's own
 *   handler (a picker closing on Esc, a grid moving on ↓) runs first and can
 *   claim the key with preventDefault — claimed events are ignored here.
 *   Browser default actions (F5 reload, Ctrl+A select-all, Ctrl+P print) run
 *   after dispatch, so preventDefault here still suppresses them.
 * - While a modal marked `data-hotkey-scope="modal"` is open, page hotkeys are
 *   paused; the modal handles its own keys (see ConfirmModal).
 * - A handler returning `false` means "not handled": the event is left alone.
 * - Letters are matched by physical key (e.code) so Alt+C works on macOS,
 *   where Option+C types "ç".
 */
import { useEffect, useRef } from "react";

export function eventCombo(e) {
    let key;
    if (/^Key[A-Z]$/.test(e.code || "")) key = e.code.slice(3).toLowerCase();
    else if (/^Digit\d$/.test(e.code || "")) key = e.code.slice(5);
    else key = (e.key || "").toLowerCase();
    const parts = [];
    if (e.ctrlKey || e.metaKey) parts.push("ctrl");
    if (e.altKey) parts.push("alt");
    // Shift alone + a letter is just a capital; with Ctrl/Alt it is a combo.
    if (e.shiftKey && (key.length > 1 || e.ctrlKey || e.metaKey || e.altKey)) parts.push("shift");
    parts.push(key);
    return parts.join("+");
}

export const modalOpen = () =>
    typeof document !== "undefined" && !!document.querySelector('[data-hotkey-scope="modal"]');

export default function useHotkeys(map, { enabled = true, when } = {}) {
    const mapRef = useRef(map);
    const whenRef = useRef(when);
    mapRef.current = map;
    whenRef.current = when;

    useEffect(() => {
        if (!enabled) return undefined;
        const onKeyDown = (e) => {
            if (e.defaultPrevented || e.isComposing) return;
            if (modalOpen()) return;
            const handler = mapRef.current?.[eventCombo(e)];
            if (!handler) return;
            if (whenRef.current && !whenRef.current(e)) return;
            if (handler(e) === false) return;
            e.preventDefault();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [enabled]);
}
